/**
 * This component renders a dashboard section with dynamic content. It utilizes the useState hook
 * to manage the state of the modal visibility and the useEffect hook to perform side effects,
 * such as fetching data when the component mounts. The component displays a title, a list of items
 * fetched from an API, and a modal that can be toggled by a button. The modal's visibility is
 * controlled by the component's state.
 *
 * Props:
 * - title: string - The title of the dashboard section.
 * - fetchData: function - A function to fetch data from an API. It should return a promise.
 *
 * State:
 * - items: array - The list of items fetched from the API.
 * - isModalOpen: boolean - A flag indicating whether the modal is open or closed.
 *
 * Effects:
 * - The component fetches data from the API when it mounts and updates the items state.
 *
 * Example usage:
 * <DashboardSection title="Example Title" fetchData={fetchDataFunction} />
 */

import React, { useEffect, useRef, useState } from "react";
import { Button, Form, FormGroup, FormSelect } from "react-bootstrap";
import ListingTable from "../../Shared/ListingTable/ListingTable";
import { motion } from "framer-motion";
import "./DashboardSection.scss";
import useTranslation from "../../../hooks/useTranslation";

const DashboardSection = ({
  tableHeaders,
  title,
  items,
  linkTo,
  handleAcknowledge,
  handleAcknowledgeCurrentPage,
  handleAcknowledgeAll,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  handleItemsPerPageChange,
  totalItems,
}) => {
  const [unit, setUnit] = useState("Celsius");
  const [height, setHeight] = useState("auto");
  const { t } = useTranslation("dashboardPage");
  /*   const [isSeeMoreButtonClicked, setIsSeeMoreButtonClicked] = useState(false);
  const sectionRef = useRef(null); */

  const convertToFahrenheit = (celsius) => {
    return (celsius * 9) / 5 + 32;
  };

  return (
    <motion.div
      className="dashboard-section"
      animate={{ height: height }} // Use the actual height of the content
      transition={{ duration: 0.8, ease: "linear" }}
    >
      {title === t("dashboard_table_title") && (
        <div className="dashboard-section__header">
          <h2 className="dashboard-section__title">{title}</h2>
          <div className="dashboard-section__acknowledge-buttons">
            <button
              className="acknowledge-btn acknowledge-btn--page"
              onClick={handleAcknowledgeCurrentPage}
              disabled={!items || items.length === 0}
            >
              <span className="acknowledge-btn__icon">✓</span>
              <span className="acknowledge-btn__text">
                {t("acknowledgeCurrentPage")}
              </span>
            </button>

            <button
              className="acknowledge-btn acknowledge-btn--all"
              onClick={handleAcknowledgeAll}
              disabled={!items || items.length === 0}
            >
              <span className="acknowledge-btn__icon">✓✓</span>
              <span className="acknowledge-btn__text">
                {t("acknowledgeAll")}
              </span>
            </button>
          </div>
          <div className="dashboard-section__selection__wrapper">
            <Form.Group
              controlId="itemsPerPageSelect"
              className="dashboard-section__items-per-page"
            >
              <Form.Label>{t("itemsPerPage")}</Form.Label>
              <FormSelect
                className="dashboard-section__items-per-page__select"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                aria-label="Items per page"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </FormSelect>
            </Form.Group>

            <Form.Group
              controlId="unitSelect"
              className="dashboard-section__unit"
            >
              <Form.Label>{t("unit")}</Form.Label>
              <FormSelect
                className="dashboard-section__unit__select"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              >
                <option value="Celsius">°C</option>
                <option value="Fahrenheit">°F</option>
              </FormSelect>
            </Form.Group>
          </div>
        </div>
      )}
      {title !== t("dashboard_table_title") && (
        <h2 className="dashboard-section__title">{title}</h2>
      )}

      <ListingTable
        tableHeaders={tableHeaders}
        tableData={items}
        /*         rowLimit={rowLimit}
         */ handleAcknowledge={handleAcknowledge}
        unit={unit}
        setUnit={setUnit}
        convertToFahrenheit={convertToFahrenheit}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        totalItems={totalItems}
        /*         isSeeMoreButtonClicked={isSeeMoreButtonClicked} */
      />
    </motion.div>
  );
};

export default DashboardSection;
